
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from '@/components/ui/button';
import { Activity, AlertCircle } from "lucide-react";
import { Job } from '@/components/jobs/types';
import { useJobActivityLog } from '@/hooks/useJobActivityLog';
import { ActivityLogItem } from './ActivityLogItem';

interface ActivityTabProps {
  job: Job;
}

export const ActivityTab: React.FC<ActivityTabProps> = ({ job }) => {
  const { data: activities, isLoading, error, refetch } = useJobActivityLog(job.id);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <Activity className="h-6 w-6 mr-2 text-primary" />
          Activity Timeline for {job.title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex items-start space-x-4">
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="space-y-2 flex-1">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        )}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error Loading Activity</AlertTitle>
            <AlertDescription>
              {error.message || "An unexpected error occurred."}
              <Button variant="link" onClick={() => refetch()} className="p-0 h-auto ml-2">Try again</Button>
            </AlertDescription>
          </Alert>
        )}
        {!isLoading && !error && activities && activities.length > 0 && (
          <div className="divide-y">
            {activities.map((activity) => (
              <ActivityLogItem key={activity.id} activity={activity} />
            ))}
          </div>
        )}
        {!isLoading && !error && (!activities || activities.length === 0) && (
          <div className="text-center py-8">
            <Activity className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No activity recorded for this job yet.</p>
            <p className="text-xs text-muted-foreground mt-1">
              Job creation, status changes, and application updates will appear here.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
